/* Copyright © 2026 Xerox Corporation. All Rights Reserved. Copyright protection claimed includes all forms and matters of copyrightable material and information now allowed by statutory or judicial law or hereinafter granted, including without limitation, material generated from the software programs which are displayed on the screen such as icons, screen display looks, etc. */

import {
  Component,
  ElementRef,
  HostListener,
  Input,
  OnInit,
  ViewChild,
} from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import {
  NgbActiveModal,
  NgbModal,
  NgbModalRef,
} from '@ng-bootstrap/ng-bootstrap';
import { AlertParams, ErrorService } from '../../services/error.service';
import { FileService } from '../../services/file.service';
import { UserService } from '../../services/user.service';
import { RunningWorkflow } from '../../services/workflow.service';
import { XdaStringsService } from '../../services/xda-strings.service';
import { ActivityIndicatorModalComponent } from './activity-indicator-modal.component';

@Component({
  selector: 'editor-modal-component',
  templateUrl: './editor-modal.component.html',
  styleUrls: ['./editor-modal.component.less'],
  standalone: false,
})
export class EditorModalComponent implements OnInit {
  @Input() workflow: RunningWorkflow;

  @ViewChild('editorFrame', { static: false })
  iframe: ElementRef<HTMLIFrameElement>;
  editorHost: string;
  safeEditorHost: SafeResourceUrl;
  modalActivityRef: NgbModalRef;
  loading = true;

  editorErrorMessages: Record<string, AlertParams>;

  constructor(
    private sanitizer: DomSanitizer,
    private modalService: NgbModal,
    private activeModal: NgbActiveModal,
    private xdaStringsService: XdaStringsService,
    private userService: UserService,
    private fileService: FileService,
    private errorService: ErrorService,
  ) {
    this.editorHost = xdaStringsService.strings['EDITORBASEURL'];
    this.safeEditorHost = this.sanitizer.bypassSecurityTrustResourceUrl(
      this.editorHost,
    );
  }

  ngOnInit(): void {
    this.loading = true;
    this.modalActivityRef = this.modalService.open(
      ActivityIndicatorModalComponent,
      {
        windowClass: 'xrx-modal transparent',
        centered: true,
        backdrop: 'static',
      },
    );

    this.editorErrorMessages = {
      'invalid domain': {
        Title: 'SDE_INVALID_DOMAIN',
        Body: this.xdaStringsService.strings['SDE_INPUT_FILES_CAN'].replace(
          '{0}',
          'services.xerox.com',
        ),
      },
      'input file not found': {
        Title: 'SDE_INPUT_FILE_NO',
        Body: this.xdaStringsService.strings[
          'SDE_UNPROCESSED_FILES_DELETED'
        ].replace('{0}', this.xdaStringsService.strings['SDE_SEVEN']),
      },
      'failed authentication': {
        Title: 'SDE_AUTHENTICATION_ERROR1',
        Body: 'SDE_INPUT_FILE_CANNOT',
      },
      'server timeout': {
        Title: 'SDE_TIMEOUT_ERROR',
        Body: 'SDE_PLEASE_TRY_AGAIN2',
      },
      'file format not supported': {
        Title: 'SDE_FILE_FORMAT_ERROR',
        Body: 'SDE_INPUT_FILES_FORMAT',
      },
      unknown: { Title: 'SDE_SYSTEM_ENCOUNTERED_ERROR1' },
    };
  }

  @HostListener('window:message', ['$event'])
  async messageRecieved(e: any) {
    if (e.origin !== this.editorHost) return;

    const msg = e.data as EditorResponse;
    if (msg.state == 'exit') {
      // Do nothing just close the modal
      this.closeEditorModal();
    } else if (msg.state == 'save') {
      this.saveEditedFile(msg.status);
    } else if (msg.state == 'ready') {
      this.modalActivityRef?.close();
      this.loading = false;
      this.loadFile();
    } else if (msg.state == 'error') {
      await this.displayErrorMessage(msg.status);
      // display an error to the user, then close the modal
      this.closeEditorModal();
    } else {
      console.error('Unknown editor state:', msg.state);
    }
  }

  saveEditedFile(url: string) {
    // Save the file back to interim, then close the modal
    this.fileService.saveEditFile(this.workflow, url).subscribe(
      () => {
        // Success now close the modal
        this.closeEditorModal('saved');
      },
      () => {
        //retry request then fail with unknown error
        this.fileService.saveEditFile(this.workflow, url).subscribe(
          () => {
            // Success now close the modal
            this.closeEditorModal('saved');
          },
          async () => {
            await this.displayErrorMessage('unknown');
            this.closeEditorModal();
          },
        );
      },
    );
  }

  displayErrorMessage(error: string): Promise<string> {
    //convert error message to lowercase and look up translated strings
    error = error.toLowerCase();
    const alertParams =
      this.editorErrorMessages[error] || this.editorErrorMessages['unknown'];
    return this.errorService.displayBasicAlert(alertParams);
  }

  closeEditorModal(reason = '') {
    this.modalActivityRef?.close();
    this.activeModal.close(reason);
  }

  loadFile() {
    const payload: EditorPayload = {
      MaxPageIncrease: 1,
      EnablePrint: true,
      EditorFeatures: 'standard',
      User: `${this.userService.user?.last_name}, ${this.userService.user?.first_name}`, //TODO: How do we want users name to appear on comments
      InputFile: this.fileService.getEditFileUrl(
        this.userService.user?.id ?? '',
        this.workflow,
      ),
    };
    this.iframe.nativeElement.contentWindow?.postMessage(
      payload,
      this.editorHost,
    );
  }
}

export interface EditorPayload {
  MaxPageIncrease: number;
  EnablePrint: boolean;
  EditorFeatures: 'standard' | string;
  User: string;
  InputFile: string;
}

export interface EditorResponse {
  state: string;
  status: string;
}
